
import React, { useRef, useEffect } from 'react';

interface ParticlesProps {
  className?: string;
}

const Particles: React.FC<ParticlesProps> = ({ className }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let particles: Array<{
      x: number;
      y: number;
      size: number;
      speedX: number;
      speedY: number;
      color: string;
      alpha: number;
      tail: boolean;
    }> = [];

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();

    // Fire colors based on the [Orange] section from the color.ini file
    const starColors = ['rgba(255, 226, 52, 0.7)']; // Star color
    const starGlowColors = ['rgba(255, 243, 173, 0.7)']; // Star glow color
    const shootingStarColors = ['rgba(255, 240, 153, 0.7)']; // Shooting star color
    const shootingStarGlowColors = ['rgba(255, 252, 234, 0.7)']; // Shooting star glow color

    // Create a standard star particle
    const createStarParticle = () => {
      const x = Math.random() * canvas.width;
      const y = Math.random() * (canvas.height / 3); // Stars mostly at top
      const size = Math.random() * 2 + 1; // Smaller stars
      const speedX = (Math.random() - 0.5) * 0.2; // Very slow horizontal movement
      const speedY = (Math.random() - 0.5) * 0.1; // Very slow vertical drift
      
      const color = starColors[Math.floor(Math.random() * starColors.length)];
      const alpha = Math.random() * 0.7 + 0.3;
      
      particles.push({ x, y, size, speedX, speedY, color, alpha, tail: false });
    };

    // Create a shooting star particle
    const createShootingStar = () => {
      // Start from top edge or right edge
      const fromTop = Math.random() > 0.5;
      let x, y;
      
      if (fromTop) {
        x = Math.random() * canvas.width;
        y = -10;
      } else {
        x = canvas.width + 10;
        y = Math.random() * (canvas.height / 2);
      }
      
      const size = Math.random() * 3 + 2; // Slightly larger than normal stars
      const speedX = -10 - Math.random() * 10; // Fast diagonal movement left
      const speedY = 5 + Math.random() * 5; // Fast diagonal movement down
      
      const color = shootingStarColors[Math.floor(Math.random() * shootingStarColors.length)];
      const alpha = Math.random() * 0.4 + 0.6;
      
      particles.push({ x, y, size, speedX, speedY, color, alpha, tail: true });
    };

    // Create a rising fire particle (from bottom up)
    const createFireParticle = () => {
      const x = Math.random() * canvas.width;
      const y = canvas.height + 10;
      const size = Math.random() * 3 + 1;
      const speedX = (Math.random() - 0.5) * 2;
      const speedY = -(Math.random() * 3 + 1);
      
      // Fire colors
      const colors = [
        'rgba(234, 56, 76, 0.7)',  // Red/neon-red
        'rgba(255, 69, 0, 0.7)',   // Orange-red
        'rgba(255, 140, 0, 0.7)',  // Dark orange
        'rgba(255, 165, 0, 0.7)',  // Orange
      ];
      
      const color = colors[Math.floor(Math.random() * colors.length)];
      const alpha = Math.random() * 0.6 + 0.4;
      
      particles.push({ x, y, size, speedX, speedY, color, alpha, tail: false });
    };

    // Occasionally create a shooting star
    const createShootingStars = () => {
      if (Math.random() < 0.005) { // Low probability for rare shooting stars
        createShootingStar();
      }
    };

    const animateParticles = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Create new regular fire particles
      if (particles.length < 150) {
        for (let i = 0; i < 5; i++) {
          createFireParticle();
        }
      }
      
      // Create background stars (constant)
      if (particles.filter(p => !p.tail && p.y < canvas.height / 2).length < 50) {
        for (let i = 0; i < 2; i++) {
          createStarParticle();
        }
      }
      
      // Occasionally create shooting stars
      createShootingStars();
      
      // Update and draw particles
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        
        p.x += p.speedX;
        p.y += p.speedY;
        p.alpha -= p.tail ? 0.02 : 0.005; // Shooting stars fade faster
        
        // Draw the particle
        ctx.beginPath();
        
        // If it's a shooting star, draw a trail
        if (p.tail && p.alpha > 0.1) {
          // Create a gradient for the tail
          const gradient = ctx.createLinearGradient(
            p.x, p.y, 
            p.x - p.speedX * 10, p.y - p.speedY * 10
          );
          gradient.addColorStop(0, p.color.replace('0.7', String(p.alpha)));
          gradient.addColorStop(1, 'transparent');
          
          // Draw the tail
          ctx.beginPath();
          ctx.strokeStyle = gradient;
          ctx.lineWidth = p.size / 2;
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(p.x - p.speedX * 10, p.y - p.speedY * 10);
          ctx.stroke();
          
          // Add a glow effect for the shooting star
          ctx.shadowBlur = 15;
          ctx.shadowColor = shootingStarGlowColors[0];
        }
        
        // Draw the actual star/particle
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = p.color.replace('0.7', String(p.alpha));
        ctx.fill();
        
        // Reset shadow settings
        ctx.shadowBlur = 0;
        
        // Remove particles that are offscreen or faded out
        if (p.y < -10 || p.y > canvas.height + 10 || 
            p.x < -10 || p.x > canvas.width + 10 || 
            p.alpha <= 0) {
          particles.splice(i, 1);
          i--;
        }
      }
      
      requestAnimationFrame(animateParticles);
    };

    animateParticles();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  return <canvas ref={canvasRef} className={className} />;
};

export default Particles;
